import { useQuery } from '@tanstack/react-query';
import { fetchPersonas, fetchPersonaById } from '../services/personaService';
import { Persona, PersonasResponse, PersonaResponse } from '../types/personas';
import { useTranslation } from 'react-i18next';

// Mock data for fallback when API calls fail or during development
export const mockPersonas: Persona[] = [
  {
    "id": "0qo14Kwxd3fp5bVQvY5g",
    "name": "Jane Doe",
    "jobTitle": "Social Media Manager",
    "bio": "Jane is a creative social media manager with 5 years of experience in digital marketing.",
    "tags": ["innovative", "trendsetter", "creative"],
    "ageRange": "25-34",
    "languages": ["en", "fr"],
    "socialNetworks": [
      { platform: "linkedin", frequency: "daily", pertinence: "Primary" },
      { platform: "instagram", frequency: "weekly", pertinence: "Secondary" },
      { platform: "twitter", frequency: "bi-weekly", pertinence: "Secondary" }
    ],
    "problems": "Struggles with maintaining a consistent brand voice across multiple platforms while keeping up with platform algorithm changes.",
    "goals": "Increase audience engagement by 20% and drive traffic to the website to improve conversion rates.",
    "motivations": "Passionate about digital innovation and building authentic connections with audiences.",
    "challenges": "Managing cross-platform content and scheduling while maintaining quality and creativity.",
    "formatLength": ["Short/Reel", "Long/Classic"],
    "brandId": "ZhbGmZGGNWmvhptEpVxQ",
    "userId": "M5P2Nn2W4veLe6FcY9FktrbzxHi1",
    "createdBy": "M5P2Nn2W4veLe6FcY9FktrbzxHi1",
    "createdAt": "2024-12-23T12:40:42.585Z",
    "updatedAt": "2024-12-23T12:40:42.585Z",
    "analysis": {
      "strengths": "Jane has a well-defined professional persona with clear goals around social media engagement. Her experience with multiple platforms provides a solid foundation for diversified content strategies. Her bilingual skills (English and French) also open opportunities for multilingual content that can reach broader audiences.",
      "weaknesses": "Could benefit from more specific platform strategies. There's also a risk of burnout without better content planning systems.",
      "contentSuggestions": [
        "Behind-the-scenes content showing the content creation process to humanize the brand",
        "Platform-specific trend analysis in short-form video to demonstrate expertise",
        "Bilingual content series addressing common social media challenges faced by your target audience",
        "Day-in-the-life content that showcases efficient workflows for multi-platform management",
        "Before/after case studies of successful engagement strategies"
      ],
      "platformRecommendations": {
        "linkedin": "Focus on visual storytelling with carousel posts for higher engagement. Use Stories for time-sensitive updates and Reels for trend participation. Consider a consistent aesthetic that aligns with brand values.",
        "twitter": "Leverage trending topics and threads for broader reach while maintaining brand voice. Focus on authentic, engaging content that feels native to the platform.",
        "facebook": "Consider adding Facebook to your platform mix for community building and engagement with a broader audience."
      },
      "audienceInsights": "This persona represents digital-savvy professionals ages 25-34 who value efficiency, innovation, and authenticity. They're likely to engage with content that solves real problems they face daily, offers actionable tips, and maintains a consistent posting schedule they can rely on. They appreciate both quick, consumable content and more detailed tutorials or guides."
    }
  },
  {
    "id": "1qo14Kwxd3fp5bVQvY5h",
    "name": "Alex Johnson",
    "jobTitle": "Content Creator",
    "bio": "Alex is a versatile content creator specializing in video production and storytelling for brands.",
    "tags": ["creative", "video", "storyteller"],
    "ageRange": "18-24",
    "languages": ["en"],
    "socialNetworks": [
      { platform: "youtube", frequency: "weekly", pertinence: "Primary" },
      { platform: "tiktok", frequency: "daily", pertinence: "Primary" },
      { platform: "instagram", frequency: "daily", pertinence: "Secondary" }
    ],
    "problems": "Finding the right balance between trending content and authentic brand messaging. Keeping up with rapidly changing platform algorithms.",
    "goals": "Build a loyal community around the brand and increase video engagement metrics by 30% quarter over quarter.",
    "motivations": "Creating content that resonates emotionally with viewers and drives meaningful conversations.",
    "challenges": "Producing high-quality video content consistently while adapting to each platform's unique requirements.",
    "formatLength": ["Short/Reel", "Video"],
    "brandId": "ZhbGmZGGNWmvhptEpVxQ",
    "userId": "M5P2Nn2W4veLe6FcY9FktrbzxHi1",
    "createdBy": "M5P2Nn2W4veLe6FcY9FktrbzxHi1",
    "createdAt": "2024-12-22T10:30:15.123Z",
    "updatedAt": "2024-12-22T10:30:15.123Z",
    "analysis": {
      "strengths": "Strong focus on video-first platforms that are growing rapidly. Alex's youth perspective helps brands connect with Gen Z audiences authentically.",
      "weaknesses": "Limited language capabilities may restrict global reach. Could benefit from more strategic planning rather than reactive content creation.",
      "contentSuggestions": [
        "Behind-the-scenes of video production to showcase the creative process",
        "Platform-specific video series tailored to each audience demographic",
        "Collaborative content with other creators to expand reach",
        "Tutorial content that showcases your expertise while providing value",
        "Day-in-the-life content that humanizes the brand"
      ],
      "platformRecommendations": {
        "youtube": "Focus on longer-form, value-driven content that establishes expertise. Consistency in posting schedule is key for algorithm performance.",
        "tiktok": "Leverage trending sounds and formats while maintaining brand voice. Quick, authentic moments perform best.",
        "instagram": "Use Stories for day-to-day engagement and Reels for broader reach. Maintain visual consistency across posts."
      },
      "audienceInsights": "This persona connects with younger audiences (18-24) who prioritize authenticity and entertainment value. They have shorter attention spans but higher engagement when content resonates. They're looking for content that feels native to each platform rather than cross-posted material."
    }
  }
];

/**
 * Hook to fetch all personas
 * @param brandId Optional brand ID to filter personas
 * @param useMockData Optional flag to force using mock data
 * @param withAnalysis Optional flag to include analysis data
 * @param appLanguage Optional language code for localized content
 * @returns Query result with personas data
 */
export const usePersonas = (
  brandId?: string, 
  useMockData: boolean = false,
  withAnalysis: boolean = false,
  appLanguage?: string
) => {
  const { i18n } = useTranslation();
  
  return useQuery<PersonasResponse, Error>({
    queryKey: ['personas', { brandId, withAnalysis, appLanguage }],
    queryFn: async () => {
      // Return mock data if explicitly requested
      if (useMockData) {
        console.log('Using mock personas data');
        // Filter by brandId if provided
        const filteredPersonas = brandId 
          ? mockPersonas.filter(persona => persona.brandId === brandId)
          : mockPersonas;
          
        return {
          success: true,
          personas: filteredPersonas,
          pagination: {
            limit: 10,
            offset: 0,
            total: filteredPersonas.length
          }
        };
      }
      
      // Otherwise fetch from API
      try {
        // Get the current language from i18n or use the provided language
        const currentLanguage = appLanguage || i18n.language || 'en';
        
        const response = await fetchPersonas(brandId, withAnalysis, currentLanguage);
        return response;
      } catch (error) {
        console.error('Error in usePersonas hook:', error);
        throw error;
      }
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 2,
    select: (data) => {
      // Sort personas by most recently updated
      return {
        ...data,
        personas: [...(data.personas || [])].sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
      };
    }
  });
};

/**
 * Hook to fetch a single persona by ID
 * @param personaId The ID of the persona to fetch
 * @param useMockData Optional flag to force using mock data
 * @returns Query result with persona data
 */
export const usePersonaById = (personaId: string | undefined, useMockData: boolean = false) => {
  return useQuery<PersonaResponse, Error>({
    queryKey: ['persona', personaId],
    queryFn: async () => {
      // Return mock data if explicitly requested or if in development mode
      if (useMockData || import.meta.env.DEV) {
        console.log('Using mock persona data for ID:', personaId);
        const persona = mockPersonas.find(p => p.id === personaId);
        
        if (!persona) {
          throw new Error(`Persona with ID ${personaId} not found`);
        }
        
        return {
          success: true,
          persona
        };
      }
      
      // Otherwise fetch from API
      return fetchPersonaById(personaId!);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    enabled: !!personaId, // Only run the query if personaId is provided
    retry: 2
  });
};

/**
 * Hook to get a summary of personas for AI analysis
 * @param personas Array of personas to analyze
 * @returns Object with summary data
 */
export const usePersonasSummary = (personas: Persona[] = []) => {
  // Calculate summary statistics
  const totalPersonas = personas.length;
  const platformCounts: Record<string, number> = {};
  const ageRanges: Record<string, number> = {};
  const languages: Record<string, number> = {};
  
  personas.forEach(persona => {
    // Count platforms
    persona.socialNetworks.forEach(network => {
      platformCounts[network.platform] = (platformCounts[network.platform] || 0) + 1;
    });
    
    // Count age ranges
    ageRanges[persona.ageRange] = (ageRanges[persona.ageRange] || 0) + 1;
    
    // Count languages
    persona.languages.forEach(lang => {
      languages[lang] = (languages[lang] || 0) + 1;
    });
  });
  
  // Generate insights based on the data
  const generateInsights = () => {
    if (totalPersonas === 0) return {
      summary: "No personas created yet. Create your first persona to get started.",
      strengths: "N/A",
      improvements: "Consider creating personas to better understand your target audience."
    };
    
    const platforms = Object.keys(platformCounts);
    const mostUsedPlatform = platforms.length > 0 
      ? platforms.reduce((a, b) => platformCounts[a] > platformCounts[b] ? a : b) 
      : 'none';
      
    const leastUsedPlatform = platforms.length > 0 
      ? platforms.reduce((a, b) => platformCounts[a] < platformCounts[b] ? a : b) 
      : 'none';
    
    return {
      summary: `You have ${totalPersonas} persona${totalPersonas !== 1 ? 's' : ''} defined, primarily focused on ${mostUsedPlatform} content. Your personas represent diverse demographics and platform preferences, enabling targeted content strategies.`,
      strengths: `Strong focus on ${mostUsedPlatform} content strategy. ${Object.keys(languages).length > 1 ? 'Multilingual capabilities across personas.' : ''} Clear target audience definitions with well-defined content format preferences.`,
      improvements: `Consider adding personas focused on ${leastUsedPlatform !== 'none' ? leastUsedPlatform : 'emerging platforms'}. Some personas could benefit from more specific KPI targets and measurement strategies.`
    };
  };
  
  return {
    totalPersonas,
    platformCounts,
    ageRanges,
    languages,
    insights: generateInsights()
  };
}; 